import { api, resolvePagination } from './api';

/**
 * Retrieve nutrition (meal) plan templates.
 */
export const getNutritionTemplates = async (pageLimit, pageToken) => {
    const headers = resolvePagination(pageToken, pageLimit);
    return api.get('/cheart/meals', { headers });
};

/**
 * Create a new nutrition (meal) plan template.
 * @param {string} name - Plan name.
 * @param {string} description - Plan description.
 * @param {object} data - JSON meal plan definition.
 */
export const createNutritionPlan = async ({ name, description, data }) => {
    const response = await api.post('/cheart/meals', {
        name,
        description,
        data
    });
    return response.data;
};

/**
 * Delete (archive) a nutrition plan.
 * @param {string} uuid - The unique identifier of the nutrition plan.
 */
export const deleteNutritionPlan = async (uuid) => {
    const response = await api.delete('/cheart/meals', {
        data: { uuid }
    });
    return response.data;
};

/**
 * Fetch a nutrition plan by UUID.
 * @param {string} uuid - The unique identifier of the nutrition plan.
 */
export const fetchNutritionPlanByUUID = async (uuid) => {
    const response = await api.post('/cheart/meals/fetch', { uuid });
    return response.data;
};

/**
 * Edit a nutrition (meal) plan template.
 * @param {string} uuid - The unique identifier of the plan.
 * @param {string} name - Plan name.
 * @param {string} description - Plan description.
 * @param {object} data - JSON meal plan definition.
 */
export const editNutritionPlan = async (uuid, name, description, data) => {
    const response = await api.patch('/cheart/meals', {
        uuid,
        name,
        description,
        data
    });
    return response.data;
};

/**
 * Search nutrition plans with a query.
 * @param {string} query - Optional search query to filter plans.
 */
export const searchNutritionPlans = async (query = '') => {
    const response = await api.post('/cheart/meals/search', { query });
    return response.data;
};

export const searchNutritionPlansPage = (query = '', pageToken, signal) =>
    api.post(
        '/cheart/meals/search',
        { query },
        {
            headers: resolvePagination(pageToken, 20),
            signal
        }
    );

export const getPatientNutritionPlans = async (patientUuid, signal) => {
    const plans = [];
    const visited = new Set();
    let token;
    do {
        const response = await api.post(
            '/cheart/patient/meals',
            { uuid: patientUuid },
            {
                headers: resolvePagination(token, 50),
                signal
            }
        );
        if (!Array.isArray(response.data)) throw new Error('Invalid nutrition plans');
        plans.push(...response.data);
        const count = Number(response.headers?.['pagination-count']);
        if (Number.isFinite(count) && plans.length >= count) break;
        token = response.headers?.['pagination-token'];
        if (!token) {
            if (Number.isFinite(count) && plans.length < count) {
                throw new Error('Incomplete nutrition plans');
            }
            break;
        }
        if (visited.has(token)) throw new Error('Repeated nutrition pagination token');
        visited.add(token);
    } while (token);
    return plans;
};

/**
 * Assign nutrition plan to a single patient
 * @param {string} planUUID - The unique identifier of the plan.
 * @param {string} patientUUID - The unique identifier of the patient.
 * @param {object} startDate - The start date of the plan.
 * @param {object} endDate - The end date of the plan.
 */
export const assignNutritionPlanToPatient = async (
    planUUID,
    patientUUID,
    startDate,
    endDate
) => {
    await api.post('/cheart/meal/assign/patients', {
        plan: planUUID,
        patients: [patientUUID],
        start_date: startDate,
        end_date: endDate
    });
};

/**
 * Assign nutrition plan to a group
 * @param {string} planUUID - The unique identifier of the plan.
 * @param {string} groupUUID - The unique identifier of the group.
 * @param {object} startDate - The start date of the plan.
 * @param {object} endDate - The end date of the plan.
 */
export const assignNutritionPlanToGroup = async (
    planUUID,
    groupUUID,
    startDate,
    endDate
) => {
    await api.post('/cheart/meal/assign/group', {
        plan: planUUID,
        group: groupUUID,
        start_date: startDate,
        end_date: endDate
    });
};
